import { useEffect, useState, } from "react";
import { RotateCcw, X, GripVertical, Eye, EyeOff } from "lucide-react";
import { DndContext, useSensor, useSensors, PointerSensor, KeyboardSensor, closestCenter } from "@dnd-kit/core";
import { verticalListSortingStrategy, useSortable, SortableContext, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableColumnItem({ column, toggleVisibility }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
        opacity: isDragging ? 0.8 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`mb-1.5 flex items-center justify-between rounded-xl border bg-white px-2.5 py-2.5 transition ${isDragging ? "border-[#50b8e7] shadow-lg ring-2 ring-[#dff2f9]" : "border-[#dce8ed] hover:border-[#b9d3de]"}`}
        >
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    aria-label={`Reorder ${column.label} column`}
                    className="cursor-grab touch-none rounded-md p-1 text-[#afc0c9] outline-none transition hover:bg-[#f0f7f9] hover:text-[#647d8e] focus-visible:ring-2 focus-visible:ring-[#bde5f5] active:cursor-grabbing"
                >
                    <GripVertical
                        size={16}
                        aria-hidden="true"
                    />
                </button>
                <span className="text-[13px] font-semibold text-[#405c6d]">{column.label}</span>
            </div>
            <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => toggleVisibility(column.id)}
                aria-label={`${column.visible ? "Hide" : "Show"} ${column.label} column`}
                aria-pressed={column.visible}
                className="rounded-md p-1.5 transition-colors hover:bg-[#f0f7f9]"
            >
                {column.visible ? (
                    <Eye size={16} className="text-[#2f84bd]" />
                ) : (
                    <EyeOff size={16} className="text-[#afc0c9]" />
                )}
            </button>
        </div>
    );
}

export default function CustomizeColumnsDropdown({ isOpen, onClose, columns, onSave, onReset }) {
    const [tempColumns, setTempColumns] = useState(columns);

    useEffect(() => {
        if (isOpen) setTempColumns(columns);
    }, [isOpen, columns]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;

        if (active.id !== over.id) {
            setTempColumns((items) => {
                const oldIndex = items.findIndex((i) => i.id === active.id);
                const newIndex = items.findIndex((i) => i.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const toggleVisibility = (id) => {
        setTempColumns((prev) =>
            prev.map((col) => (col.id === id ? { ...col, visible: !col.visible } : col))
        );
    };

    const handleSave = () => {
        onSave(tempColumns);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="absolute right-0 top-11 z-50 flex w-72 flex-col overflow-hidden rounded-2xl border border-[#d6e5eb] bg-white shadow-[0_20px_50px_-18px_rgba(15,43,66,0.35)]">
            <div className="flex items-start justify-between border-b border-[#e5eef2] px-4 py-3.5">
                <div>
                    <h3 className="text-[15px] font-bold text-[#173044]">Table columns</h3>
                    <p className="mt-0.5 text-xs text-[#6d8493]">Drag to reorder or hide columns.</p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close column settings"
                    className="rounded-md p-1 text-[#8197a4] transition hover:bg-[#f0f7f9] hover:text-[#405c6d]"
                >
                    <X size={18} />
                </button>
            </div>

            <div className="max-h-[360px] overflow-y-auto bg-[#f7fafb] p-2 custom-scrollbar">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={tempColumns.map(c => c.id)} strategy={verticalListSortingStrategy}>
                        {tempColumns.map((col) => (
                            <SortableColumnItem
                                key={col.id}
                                column={col}
                                toggleVisibility={toggleVisibility}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>

            <div className="flex items-center justify-between border-t border-[#d6e5eb] bg-white px-3 py-2.5">
                <button type="button" onClick={() => { onReset(); onClose(); }} className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-[13px] font-semibold text-[#6d8493] transition hover:bg-[#f0f7f9] hover:text-[#405c6d]">
                    <RotateCcw size={14} />
                    Reset
                </button>
                <div className="flex gap-2">
                    <button type="button" onClick={onClose} className="h-9 rounded-lg px-3 text-[13px] font-semibold text-[#526e7e] transition hover:bg-[#f0f7f9]">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSave} className="h-9 rounded-xl bg-gradient-to-b from-[#388fbd] to-[#176b9a] px-3.5 text-[13px] font-bold text-white shadow-[0_6px_14px_-8px_rgba(14,104,153,0.7)] transition hover:brightness-105">
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
}
